/* ==========================================================================
   wango-gl — a ~150-line fullscreen-quad WebGL runner.

   The Next.js build used three.js for four effects (Origin hero dissolve,
   wordmark video mask, displacement crossfade, SDF lens). Every one of them
   is an orthographic camera pointed at a single PlaneGeometry(2,2) with a
   ShaderMaterial — i.e. exactly a fullscreen quad. Shipping 385 KB of three.js
   to a storefront to draw one quad is not a trade worth making on a shop that
   is judged on Core Web Vitals, so the GLSL is carried over verbatim and only
   the plumbing is replaced.

   Differences from three.js that the ported shaders rely on:
     • `varying vec2 vUv` and the `uv` attribute are provided here too.
     • three.js prepends `projectionMatrix`/`modelViewMatrix`; the vertex
       shader below writes gl_Position directly instead, so the ported
       fragment shaders are unchanged but their vertex shaders are not used.
     • Textures default to CLAMP_TO_EDGE + LINEAR, matching three.js defaults.
   ========================================================================== */
(function (root) {
  "use strict";

  var VERT =
    "attribute vec2 aPos; varying vec2 vUv;" +
    "void main(){ vUv = aPos * 0.5 + 0.5; gl_Position = vec4(aPos, 0.0, 1.0); }";

  function compile(gl, type, src) {
    var s = gl.createShader(type);
    gl.shaderSource(s, src);
    gl.compileShader(s);
    if (!gl.getShaderParameter(s, gl.COMPILE_STATUS)) {
      // Surfacing this is worth the console noise: a silent shader failure
      // renders a blank black box that looks exactly like "the image didn't load".
      console.error("[wango-gl] shader:", gl.getShaderInfoLog(s), src);
      gl.deleteShader(s);
      return null;
    }
    return s;
  }

  /**
   * @param {HTMLCanvasElement} canvas
   * @param {{frag:string, uniforms:Object}} opts  uniform values are numbers,
   *        [x,y] arrays, or {texture:<img|video|canvas>} descriptors.
   * @returns {?Object} null when WebGL is unavailable — callers must fall back.
   */
  function quad(canvas, opts) {
    /* no MSAA: a single full-screen triangle has no edges to smooth, and the
       multisampled buffer cost a resolve pass on every frame */
    var gl = canvas.getContext("webgl", { alpha: true, antialias: false, premultipliedAlpha: false });
    if (!gl) return null;

    var vs = compile(gl, gl.VERTEX_SHADER, VERT);
    var fs = compile(gl, gl.FRAGMENT_SHADER, "precision highp float;\n" + opts.frag);
    if (!vs || !fs) return null;

    var prog = gl.createProgram();
    gl.attachShader(prog, vs);
    gl.attachShader(prog, fs);
    gl.linkProgram(prog);
    if (!gl.getProgramParameter(prog, gl.LINK_STATUS)) {
      console.error("[wango-gl] link:", gl.getProgramInfoLog(prog));
      return null;
    }
    gl.useProgram(prog);

    var buf = gl.createBuffer();
    gl.bindBuffer(gl.ARRAY_BUFFER, buf);
    gl.bufferData(gl.ARRAY_BUFFER, new Float32Array([-1, -1, 3, -1, -1, 3]), gl.STATIC_DRAW);
    var loc = gl.getAttribLocation(prog, "aPos");
    gl.enableVertexAttribArray(loc);
    gl.vertexAttribPointer(loc, 2, gl.FLOAT, false, 0, 0);

    gl.enable(gl.BLEND);
    gl.blendFunc(gl.SRC_ALPHA, gl.ONE_MINUS_SRC_ALPHA);

    var locs = {};
    function uloc(name) {
      if (!(name in locs)) locs[name] = gl.getUniformLocation(prog, name);
      return locs[name];
    }

    /* Resolution cap. Callers pass the cap the React build used for that
       effect (the hero and the wordmark never called setPixelRatio, i.e. 1x;
       the rest used min(devicePixelRatio, 2)). On top of that the cap steps
       down when frames run long, so a heavy full-screen shader on a Retina
       panel degrades resolution instead of frame rate. */
    var cap = opts.maxDpr || 2, lastW = 1, lastH = 1;
    var visible = true, lastT = 0, ema = 16.7, samples = 0;
    if (window.IntersectionObserver) {
      new IntersectionObserver(function (es) {
        visible = es[es.length - 1].isIntersecting;
        lastT = 0;                       /* a gap while hidden is not a slow frame */
        if (visible) changed = true;
      }, { rootMargin: "120px" }).observe(canvas);
    }

    var textures = {};   // name -> {tex, unit, source, dynamic}
    var nextUnit = 0;
    var values = {};
    /* Draw only when something on screen would differ: a uniform that really
       changed, a texture, a resize, a new video frame. Callers run a rAF loop
       and call render() every frame; an idle effect now costs nothing instead
       of a full-screen shader pass 60 times a second. */
    var changed = true;
    /* Still images keep their GL texture, keyed by the source element, so a
       picture shown again (the crossfade's pairs) is re-bound, not re-uploaded:
       a texImage2D of a 2400px photo stalled the frame it happened on. */
    var stills = [];   // [{source, tex}]
    function stillTexture(source) {
      for (var i = 0; i < stills.length; i++) if (stills[i].source === source) return stills[i].tex;
      var tex = makeTexture(source);
      stills.push({ source: source, tex: tex });
      return tex;
    }
    function same(a, b) {
      if (Array.isArray(a) && Array.isArray(b)) {
        if (a.length !== b.length) return false;
        for (var i = 0; i < a.length; i++) if (a[i] !== b[i]) return false;
        return true;
      }
      return a === b;
    }

    function makeTexture(source) {
      var tex = gl.createTexture();
      gl.bindTexture(gl.TEXTURE_2D, tex);
      gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_S, gl.CLAMP_TO_EDGE);
      gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_T, gl.CLAMP_TO_EDGE);
      gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MIN_FILTER, gl.LINEAR);
      gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MAG_FILTER, gl.LINEAR);
      gl.pixelStorei(gl.UNPACK_FLIP_Y_WEBGL, true);
      upload(tex, source);
      return tex;
    }

    function upload(tex, source) {
      gl.bindTexture(gl.TEXTURE_2D, tex);
      gl.pixelStorei(gl.UNPACK_FLIP_Y_WEBGL, true);
      try {
        gl.texImage2D(gl.TEXTURE_2D, 0, gl.RGBA, gl.RGBA, gl.UNSIGNED_BYTE, source);
      } catch (e) {
        /* A cross-origin image without CORS headers taints the upload. The
           draw is simply skipped rather than throwing every frame. */
      }
    }

    function set(name, value) {
      if (value && value.texture) {
        values[name] = value;
        changed = true;
        var dyn = !!value.dynamic, t = textures[name];
        if (!t) {
          t = textures[name] = { tex: dyn ? makeTexture(value.texture) : stillTexture(value.texture), unit: nextUnit++, source: value.texture, dynamic: dyn };
        } else if (dyn) {
          if (!t.dynamic || t.source !== value.texture) t.tex = makeTexture(value.texture);
          t.source = value.texture; t.dynamic = true;
        } else if (t.source !== value.texture || t.dynamic) {
          t.tex = stillTexture(value.texture);
          t.source = value.texture; t.dynamic = false;
        }
        /* requestVideoFrameCallback fires once per frame the video actually
           presents (including after a seek), so the texture is re-uploaded
           only then instead of gating on readyState, which sits at 1 for the
           whole of a seek and starved the upload. */
        if (t.dynamic && !t.rvfc && t.source.requestVideoFrameCallback) {
          t.rvfc = true; t.dirty = true;
          var src = t.source;
          (function again() { t.dirty = true; src.requestVideoFrameCallback(again); })();
        }
        return;
      }
      if (same(values[name], value)) return;
      values[name] = Array.isArray(value) ? value.slice() : value;
      changed = true;
      gl.useProgram(prog);
      var l = uloc(name);
      if (l === null) return;
      if (Array.isArray(value)) {
        if (value.length === 2) gl.uniform2f(l, value[0], value[1]);
        else if (value.length === 3) gl.uniform3f(l, value[0], value[1], value[2]);
        else gl.uniform4f(l, value[0], value[1], value[2], value[3]);
      } else {
        gl.uniform1f(l, value);
      }
    }

    function resize(w, h, dpr) {
      lastW = w; lastH = h;
      var d = dpr || Math.min(window.devicePixelRatio || 1, cap);
      canvas.width = Math.max(1, Math.round(w * d));
      canvas.height = Math.max(1, Math.round(h * d));
      gl.viewport(0, 0, canvas.width, canvas.height);
      changed = true;
    }

    function render() {
      if (!visible) return;              /* nothing to see: skip the draw */
      var fresh = false;
      for (var n in textures) {
        var tx = textures[n];
        if (tx.dynamic && tx.source && tx.source.readyState >= 2 && (!tx.rvfc || tx.dirty)) { fresh = true; break; }
      }
      if (!changed && !fresh) { lastT = 0; return; }   /* same picture as last frame */
      changed = false;
      var now = performance.now();
      if (lastT) {
        ema += (Math.min(now - lastT, 100) - ema) * 0.08;
        if (++samples > 90 && ema > 21 && cap > 1) { cap = Math.max(1, cap - 0.5); samples = 0; ema = 16.7; resize(lastW, lastH); }
      }
      lastT = now;
      gl.useProgram(prog);
      var i = 0;
      for (var name in textures) {
        var t = textures[name];
        gl.activeTexture(gl.TEXTURE0 + t.unit);
        gl.bindTexture(gl.TEXTURE_2D, t.tex);
        // A <video> keeps producing new frames, so its texture is re-uploaded
        // every draw; still images are uploaded once.
        if (t.dynamic && t.source && t.source.readyState >= 2 && (!t.rvfc || t.dirty)) { upload(t.tex, t.source); t.dirty = false; }
        var l = uloc(name);
        if (l !== null) gl.uniform1i(l, t.unit);
        i++;
      }
      gl.clearColor(0, 0, 0, 0);
      gl.clear(gl.COLOR_BUFFER_BIT);
      gl.drawArrays(gl.TRIANGLES, 0, 3);
    }

    function dispose() {
      for (var name in textures) gl.deleteTexture(textures[name].tex);
      gl.deleteBuffer(buf);
      gl.deleteProgram(prog);
      gl.deleteShader(vs);
      gl.deleteShader(fs);
      var ext = gl.getExtension("WEBGL_lose_context");
      if (ext) ext.loseContext();
    }

    if (opts.uniforms) for (var k in opts.uniforms) set(k, opts.uniforms[k]);

    /** Uploads a still ahead of time (call it when the page is idle), so the
        frame that first shows it does not pay for the upload. */
    function warm(source) { stillTexture(source); }

    return { gl: gl, set: set, resize: resize, render: render, dispose: dispose, values: values, warm: warm };
  }

  /** Loads an image and resolves with it, or rejects — never hangs. */
  function loadImage(src) {
    return new Promise(function (resolve, reject) {
      var img = new Image();
      img.crossOrigin = "anonymous";
      /* decode off the main thread before anyone uploads it; otherwise the
         decode happens inside texImage2D, on the frame that first draws it */
      img.onload = function () {
        if (img.decode) img.decode().then(function () { resolve(img); }, function () { resolve(img); });
        else resolve(img);
      };
      img.onerror = reject;
      img.src = src;
    });
  }

  root.WangoGL = { quad: quad, loadImage: loadImage };
})(window);
